import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { ICrossAppMessage, ICrossAppMessageHandler, SupportedCrossAppEventType, ICrossAppMessageData, SupportActionEvents } from '@newroom-connect/library/interfaces';

import { LoggingService } from '../logging/logging.service';

@Injectable({
  providedIn: 'root'
})
export class CrossAppMessageService implements OnDestroy {
  protected readonly logger = inject(LoggingService);

  private static readonly CROSS_APP_MESSAGE = 'message';

  private messageSubject = new Subject<ICrossAppMessage>();
  private handlers: ICrossAppMessageHandler[] = [];
  private isConnected = signal<boolean>(false);

  /**
   * @constructor
   */
  constructor() {
    if (this.isConnected()) {
      return;
    }

    this.initializeMessageListener();
    this.isConnected.set(true);
  }

  /**
   * Clean up resources when the service is destroyed.
   */
  public ngOnDestroy(): void {
    window.removeEventListener(CrossAppMessageService.CROSS_APP_MESSAGE, this.handleMessage.bind(this));
    this.messageSubject.complete();
  }

  /**
   * Register a handler for specific message types.
   *
   * @param type The type of message to handle.
   * @param callback The callback function to execute when a message of the specified type is received.
   */
  public registerHandler(type: string, callback: (message: ICrossAppMessage) => void): void {
    this.handlers.push({ type, callback });
  }

  /**
   * Remove a specific message handler.
   *
   * @param type The type of message the handler was registered for.
   * @param callback The callback function to remove.
   */
  public removeHandler(type: string, callback: (message: ICrossAppMessage) => void): void {
    this.handlers = this.handlers.filter(
      handler => !(handler.type === type && handler.callback === callback)
    );
  }

  /**
   * Send a message cross-apps.
   *
   * @param type
   * @param data The partial data to be sent.
   */
  public sendMessage(type: SupportedCrossAppEventType, data: ICrossAppMessageData): void {
    // Example of crossAppMessage to be sent to other applications running within the current app.
    const fullMessage: ICrossAppMessage = {
      type,
      data,
      source: CrossAppMessageService.CROSS_APP_MESSAGE,
      timestamp: Date.now()
    };

    window.postMessage(fullMessage, '*');
  }

  /**
   * Initialize the nrc-app event listener for cross-app communication.
   */
  private initializeMessageListener(): void {
    window.addEventListener(CrossAppMessageService.CROSS_APP_MESSAGE, this.handleMessage.bind(this));
  }

  /**
   * Handle incoming messages and execute registered handlers.
   *
   * @param event The message event containing the cross-app message.
   */
  private handleMessage(event: MessageEvent): void {
    if (!CrossAppMessageService.isValidCrossAppMessageEvent(event)) {
      return;
    }

    const crossAppEventData = event.data;

    if (CrossAppMessageService.isValidCrossAppMessage(crossAppEventData)) {
      try {
        const message: ICrossAppMessage = {
          type: crossAppEventData.type,
          data: crossAppEventData.data,
          source: crossAppEventData.source,
          timestamp: Date.now()
        };

        this.messageSubject.next(message);

        // Execute registered handlers
        this.handlers
          .filter(handler => handler.type === message.type)
          .forEach(handler => handler.callback(message));
      } catch (error) {
        this.logger.error('Error processing message:', error);
      }
    } else {
      this.logger.error('Invalid cross-app message:', crossAppEventData);
    }
  }

  /**
   * Validates the type of the CrossAppMessage.
   * @param rawMessage The raw Message recieved from CrossApp.
   * @returns A boolean indicating whether the message is valid or not.
   */
  public static isValidCrossAppMessage(rawMessage: unknown): rawMessage is ICrossAppMessage {
    return !!rawMessage
      && typeof rawMessage === 'object'
      && 'data' in rawMessage
      && CrossAppMessageService.isValidCrossAppMessageData(rawMessage.data);
  }

  /**
   * Validates the data of the CrossAppMessage.
   *
   * @param data
   *
   * @returns A boolean indicating whether the message is valid or not.
   */
  public static isValidCrossAppMessageData(data: unknown): data is ICrossAppMessageData {
    return !!data
      && typeof data === 'object'
      && 'action' in data
      && Object.values(SupportActionEvents).includes(data.action as SupportActionEvents)
      && this.isValidCrossAppMessageDataForAction(data);
  }

  /**
   * Validates if the message data fits the expected format of corresponding action.
   *
   * @param messageData
   *
   * @returns A boolean indicating whether the message data is valid or not.
   */
  private static isValidCrossAppMessageDataForAction(messageData: unknown): boolean {
    if (typeof messageData !== 'object' || !messageData || !('action' in messageData)) {
      return false;
    }

    switch (messageData.action) {
      case SupportActionEvents.ACTIVATE_AREA_ELEMENT || SupportActionEvents.HIDE_AREA_ELEMENTS || SupportActionEvents.SHOW_AREA_ELEMENTS:
        if (!('areaElementIds' in messageData) || !messageData.areaElementIds) {
          return false;
        }
        break;

      case SupportActionEvents.HIDE_ALL_AREA_ELEMENTS || SupportActionEvents.SHOW_ALL_AREA_ELEMENTS:
        break;

      case SupportActionEvents.INITIALIZE_AREA:
        if (!('htmlBackgroundElementIds' in messageData) || !messageData.htmlBackgroundElementIds) {
          return false;
        }
        break;

      case SupportActionEvents.CREATE_TRACKING_EVENT:
        if (!('metadata' in messageData) || !messageData.metadata) {
          return false;
        }
        break;

      default:
        return false;
    }

    return true;
  }

  /**
   * Validates the type of a Window MessageEvent is either a HTML_BACKGROUND or PIXEL_STREAM.
   *
   * @param event The Window MessageEvent.
   *
   * @returns
   */
  private static isValidCrossAppMessageEvent(event: MessageEvent): boolean {
    return !!event
      && 'data' in event
      && typeof event.data === 'object'
      && 'type' in event.data
      && typeof event.data.type === 'string'
      && Object.values(SupportedCrossAppEventType).includes(event.data.type as SupportedCrossAppEventType);
  }
}
